import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom'; // Added RouterLink
import { Container, Grid, Card, CardMedia, Typography, Rating, Chip, Button, Box, Stack, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles'; // Added useTheme
import { MapPin, Phone, Mail } from 'lucide-react';
import { mockAgents as agents, mockProperties, mockAgencies, AgencyType } from '../utils/mockData'; // Added mockAgencies, AgencyType
import PropertyCard, { PropertyType } from '../components/property/PropertyCard'; 

const AgentDetailPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const theme = useTheme(); // Added theme
  const agent = agents.find(a => a.id === parseInt(agentId || ''));

  if (!agent) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" align="center">Agent not found</Typography>
      </Container>
    );
  }

  const agency = agent.agencyId ? mockAgencies.find(ag => ag.id === agent.agencyId) : undefined;

  const agentProperties = mockProperties.filter(
    (property) => property.agentId === agent.id
  );

  // The redundant !agent check was removed as it's covered by the first one.

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardMedia
                component="img"
                height="400"
                image={agent.image}
                alt={agent.name}
                sx={{ objectFit: 'cover' }}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="h4" component="h1" gutterBottom>
              {agent.name}
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {agent.title}
            </Typography>

            {agency && (
              <Typography variant="subtitle1" gutterBottom sx={{mb: 2}}>
                Affiliated with: <RouterLink to={`/agencies/${agency.id}`} style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 'medium' }}>{agency.name}</RouterLink>
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Rating value={agent.rating} precision={0.1} readOnly />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({agent.reviews} reviews)
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body1">
                  <strong>Listings:</strong> {agent.listings}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1">
                  <strong>Experience:</strong> {agent.experience}
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
                Specialties
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {agent.specialties.map((specialty) => (
                  <Chip key={specialty} label={specialty} />
                ))}
              </Stack>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MapPin size={20} style={{ marginRight: '8px' }} />
                  <Typography variant="body1">{agent.location}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone size={20} style={{ marginRight: '8px' }} />
                  <Typography variant="body1" sx={{ mr: 2 }}>{agent.phone}</Typography>
                  <Button variant="outlined" size="small" href={`tel:${agent.phone}`}>
                    Call
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Mail size={20} style={{ marginRight: '8px' }} />
                  <Typography variant="body1" sx={{ mr: 2 }}>{agent.email}</Typography>
                  <Button variant="outlined" size="small" href={`mailto:${agent.email}`}>
                    Email
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mt: 5, py: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
          Properties Managed by {agent.name}
        </Typography>
        {agentProperties.length > 0 ? (
          <Grid container spacing={3}>
            {agentProperties.map((property) => (
              <Grid item xs={12} sm={6} md={4} key={property.id}>
                <PropertyCard property={property} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">
              {agent.name} has no properties listed at the moment.
            </Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default AgentDetailPage;
